import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, SendHorizontal, Sparkles } from "lucide-react";
import { site } from "@/config/site";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GREETING = {
  role: "assistant",
  content:
    "Hi! I'm Ember, North Ember Painting's AI assistant. Ask me about our painting services, the areas we cover, or how a free estimate works.",
};

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => {
    let id = null;
    try {
      id = localStorage.getItem("ne-chat-session");
      if (!id) {
        id = crypto.randomUUID();
        localStorage.setItem("ne-chat-session", id);
      }
    } catch {
      id = crypto.randomUUID();
    }
    return id;
  });
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const patchLast = (content) =>
    setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { role: "assistant", content };
      return copy;
    });

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    trackEvent("chat_message");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let payload;
          try {
            payload = JSON.parse(line.slice(5).trim());
          } catch {
            continue;
          }
          if (payload.t) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = {
                role: "assistant",
                content: copy[copy.length - 1].content + payload.t,
              };
              return copy;
            });
          }
          if (payload.error) patchLast(payload.error);
        }
      }
      setMessages((m) => {
        const last = m[m.length - 1];
        if (last && last.role === "assistant" && !last.content) {
          const copy = [...m];
          copy[copy.length - 1] = {
            role: "assistant",
            content: `I didn't catch a response there — please try again, or call us any time at ${site.phone}.`,
          };
          return copy;
        }
        return m;
      });
    } catch {
      patchLast(`Sorry, I couldn't respond just now. You can reach us any time at ${site.phone} or ${site.email}.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="chat-toggle"
        aria-expanded={open}
        aria-label={open ? "Close chat assistant" : "Open chat assistant"}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-[92px] right-4 z-[65] inline-flex h-14 w-14 items-center justify-center rounded-full bg-ember text-white shadow-xl shadow-orange-900/40 transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-ember-dark lg:bottom-6 lg:right-6"
      >
        {open ? <X className="h-6 w-6" aria-hidden="true" /> : <MessageCircle className="h-6 w-6" aria-hidden="true" />}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Ember — AI painting assistant"
          data-testid="chat-panel"
          className="fixed bottom-[158px] right-4 z-[65] flex h-[520px] max-h-[62vh] w-[min(92vw,384px)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl lg:bottom-24 lg:right-6"
        >
          <div className="flex items-center gap-3 bg-ink px-5 py-4 text-white">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-ember/15 text-ember">
              <Sparkles className="h-4 w-4" aria-hidden="true" />
            </span>
            <div>
              <p className="font-display text-sm font-bold">Ember</p>
              <p className="text-[11px] uppercase tracking-widest text-slate-400">AI assistant · North Ember Painting</p>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-5" data-testid="chat-messages" aria-live="polite">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-message-${i}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "rounded-br-sm bg-ember text-white"
                      : "rounded-bl-sm bg-slate-100 text-slate-700"
                  }`}
                >
                  {m.content || (busy && m.role === "assistant" && i === messages.length - 1 ? "…" : m.content)}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={send} className="border-t border-slate-200 p-3">
            <div className="flex items-center gap-2">
              <label htmlFor="chat-input" className="sr-only">Ask the assistant</label>
              <input
                id="chat-input"
                ref={inputRef}
                data-testid="chat-input"
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about services, areas, estimates…"
                maxLength={1000}
                className="min-h-[44px] flex-1 rounded-full border border-slate-300 px-4 text-sm text-slate-900 placeholder:text-slate-400 focus:border-ember focus:outline-none focus:ring-2 focus:ring-ember/25"
              />
              <button
                type="submit"
                data-testid="chat-send"
                disabled={busy || !input.trim()}
                aria-label="Send message"
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ember text-white transition-colors duration-200 hover:bg-ember-dark disabled:opacity-50"
              >
                <SendHorizontal className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <p className="mt-2 px-1 text-[10px] leading-relaxed text-slate-400">
              AI assistant — answers may be imperfect. For a firm quote, use the free estimate form or call{" "}
              {site.phone}.
            </p>
          </form>
        </div>
      )}
    </>
  );
}
